import React, { useEffect, useState } from 'react';
import { Box, Typography, Paper, Button, TextField, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, IconButton } from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import EmojiPicker from 'emoji-picker-react';
import { SketchPicker } from 'react-color';
import { API_ENDPOINTS } from '../../config/api';
import Checkbox from '@mui/material/Checkbox';
import FormControlLabel from '@mui/material/FormControlLabel';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import { toast } from 'react-toastify';
import CheckIcon from '@mui/icons-material/Check';
import ShopIcon from '@mui/icons-material/Shop';
import SyncIcon from '@mui/icons-material/Sync';

const PlanSettings = () => {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [featureInputs, setFeatureInputs] = useState({}); // { planId: { key: '', value: '' } }
  const [userEmail, setUserEmail] = useState('');
  const [newPlan, setNewPlan] = useState({ title: '', price_monthly: '', price_yearly: '', color: '', emoji: '' });
  const [editPlanId, setEditPlanId] = useState(null);
  const [editPlan, setEditPlan] = useState({ title: '', price_monthly: '', price_yearly: '', color: '', emoji: '' });
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [colorPickerOpen, setColorPickerOpen] = useState(false);
  const [editColorPickerOpen, setEditColorPickerOpen] = useState(false);
  const [emojiPickerOpen, setEmojiPickerOpen] = useState(false);
  const [editEmojiPickerOpen, setEditEmojiPickerOpen] = useState(false);
  const [syncAllFeatures, setSyncAllFeatures] = useState({}); // { planId: true/false }
  const [featureModalOpen, setFeatureModalOpen] = useState(false);
  const [featureKey, setFeatureKey] = useState('');
  const [featureValue, setFeatureValue] = useState('');
  const [featureSyncAll, setFeatureSyncAll] = useState(true);
  const [featureEditInputs, setFeatureEditInputs] = useState({}); // { planId: { [featureKey]: value } }
  const [featureEditLoading, setFeatureEditLoading] = useState({}); // { planId: { [featureKey]: bool } }
  const [featureDeleteUndo, setFeatureDeleteUndo] = useState(null); // { planId, featureKey, featureValue }
  const [planDeleteUndo, setPlanDeleteUndo] = useState(null); // { planId, planData }
  const [deleteFeatureDialog, setDeleteFeatureDialog] = useState({ open: false, planId: null, featureKey: null });
  const [deletePlanDialog, setDeletePlanDialog] = useState({ open: false, planId: null });
  const [shopierLoading, setShopierLoading] = useState({});

  useEffect(() => {
    // Kullanıcı emailini localStorage'dan al
    const userData = localStorage.getItem('userData');
    if (userData) {
      setUserEmail(JSON.parse(userData).email);
    }
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    setLoading(true);
    try {
      const res = await fetch(API_ENDPOINTS.plans.getAll(), {
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (!res.ok) throw new Error('Planlar alınamadı');
      const data = await res.json();
      setPlans(data);
    } catch (err) {
      alert('Planlar alınamadı: ' + (err.message));
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (planId, field, value) => {
    setFeatureInputs(prev => ({
      ...prev,
      [planId]: {
        ...prev[planId],
        [field]: value
      }
    }));
  };

  const handleSyncAllChange = (planId, checked) => {
    setSyncAllFeatures(prev => ({ ...prev, [planId]: checked }));
  };

  const handleAddOrUpdateFeature = async (planId) => {
    const { key, value } = featureInputs[planId] || {};
    if (!key) return alert('Özellik anahtarı girin');
    try {
      const res = await fetch(API_ENDPOINTS.plans.addFeature(planId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          feature_key: key,
          feature_value: value,
          syncAll: syncAllFeatures[planId] !== false // undefined veya true ise true
        })
      });
      if (!res.ok) throw new Error('Özellik eklenemedi');
      fetchPlans();
      setFeatureInputs(prev => ({ ...prev, [planId]: { key: '', value: '' } }));
    } catch (err) {
      alert('Özellik eklenemedi: ' + (err.message));
    }
  };

  const handleDeleteFeature = (planId, featureKey) => {
    setDeleteFeatureDialog({ open: true, planId, featureKey });
  };

  const confirmDeleteFeature = async () => {
    const { planId, featureKey } = deleteFeatureDialog;
    setDeleteFeatureDialog({ open: false, planId: null, featureKey: null });
    // Özellik silme işlemi için toast ile uyarı ve undo imkanı
    const plan = plans.find(p => p.id === planId);
    const feature = plan?.features.find(f => f.feature_key === featureKey);
    if (!feature) return toast.error('Özellik bulunamadı');
    setFeatureDeleteUndo({ planId, featureKey, featureValue: feature.feature_value });
    toast.info(
      <span>
        Özellik silindi. <Button color="inherit" size="small" onClick={() => handleUndoDeleteFeature(planId, featureKey, feature.feature_value)}>Geri Al</Button>
      </span>,
      { autoClose: 5000 }
    );
    try {
      const syncAll = syncAllFeatures[planId] !== false;
      const res = await fetch(API_ENDPOINTS.plans.deleteFeature(planId, featureKey) + `?syncAll=${syncAll}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (!res.ok) throw new Error('Özellik silinemedi');
      fetchPlans();
    } catch (err) {
      toast.error('Özellik silinemedi: ' + (err.message));
    }
  };

  const handleUndoDeleteFeature = async (planId, featureKey, featureValue) => {
    try {
      // syncAll kontrolü: Eğer syncAllFeatures[planId] !== false ise tüm planlara uygula
      const syncAll = syncAllFeatures[planId] !== false;
      if (syncAll) {
        // Tüm planlara uygula
        await Promise.all(
          plans.map(async (plan) => {
            const res = await fetch(API_ENDPOINTS.plans.addFeature(plan.id), {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                'Authorization': `Bearer ${localStorage.getItem('authToken')}`
              },
              body: JSON.stringify({
                feature_key: featureKey,
                feature_value: featureValue,
                syncAll: false
              })
            });
            if (!res.ok) throw new Error('Geri alma başarısız');
          })
        );
        toast.success('Özellik tüm paketlerde geri alındı');
      } else {
        // Sadece ilgili planda uygula
        const res = await fetch(API_ENDPOINTS.plans.addFeature(planId), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify({
            feature_key: featureKey,
            feature_value: featureValue,
            syncAll: false
          })
        });
        if (!res.ok) throw new Error('Geri alma başarısız');
        toast.success('Özellik geri alındı');
      }
      fetchPlans();
    } catch (err) {
      toast.error('Geri alma başarısız: ' + (err.message));
    } finally {
      setFeatureDeleteUndo(null);
    }
  };

  const handleDeletePlan = (planId) => {
    setDeletePlanDialog({ open: true, planId });
  };

  const confirmDeletePlan = async () => {
    const { planId } = deletePlanDialog;
    setDeletePlanDialog({ open: false, planId: null });
    // Plan silme işlemi için toast ile uyarı ve undo imkanı
    const plan = plans.find(p => p.id === planId);
    if (!plan) return toast.error('Plan bulunamadı');
    setPlanDeleteUndo({ planId, planData: plan });
    toast.info(
      <span>
        {plan.title} silindi/pasif yapıldı. <Button color="inherit" size="small" onClick={() => handleUndoDeletePlan(planId, plan)}>Geri Al</Button>
      </span>,
      { autoClose: 5000 }
    );
    try {
      const res = await fetch(API_ENDPOINTS.plans.delete(planId), {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${localStorage.getItem('authToken')}` }
      });
      if (!res.ok) throw new Error('Plan silinemedi/pasif edilemedi');
      const data = await res.json();
      fetchPlans();
      if (data.deleted) {
        toast.success('Plan tamamen silindi.');
      } else if (data.passive) {
        toast.success('Plan kullanıcıda tanımlı olduğu için pasif hale getirildi.');
      }
    } catch (err) {
      toast.error('Plan silinemedi/pasif edilemedi: ' + (err.message));
    }
  };

  const handleUndoDeletePlan = async (planId, planData) => {
    try {
      // Plan tamamen silindiyse tekrar ekle, pasif olduysa tekrar aktif yap
      if (planData.is_active === false) {
        // Sadece aktiflik güncellemesi
        const res = await fetch(API_ENDPOINTS.plans.update(planId), {
          method: 'PUT',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify({ ...planData, is_active: true })
        });
        if (!res.ok) throw new Error('Geri alma başarısız');
        toast.success('Plan tekrar aktif edildi');
      } else {
        // Tamamen silindiyse tekrar ekle (id olmadan)
        const { id, ...planToCreate } = planData;
        const res = await fetch(API_ENDPOINTS.plans.create(), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${localStorage.getItem('authToken')}`
          },
          body: JSON.stringify(planToCreate)
        });
        if (!res.ok) throw new Error('Geri alma başarısız');
        toast.success('Plan geri alındı (yeniden eklendi)');
      }
      fetchPlans();
    } catch (err) {
      toast.error('Geri alma başarısız: ' + (err.message));
    } finally {
      setPlanDeleteUndo(null);
    }
  };

  const handleNewPlanChange = (field, value) => {
    setNewPlan(prev => ({ ...prev, [field]: value }));
  };

  const handleOpenAddModal = () => setAddModalOpen(true);
  const handleCloseAddModal = () => setAddModalOpen(false);
  const handleOpenEditModal = () => setEditModalOpen(true);
  const handleCloseEditModal = () => setEditModalOpen(false);

  const handleColorChange = (color) => {
    setNewPlan(prev => ({ ...prev, color: color.hex }));
    // setColorPickerOpen(false); // Otomatik kapanma kaldırıldı
  };
  const handleEditColorChange = (color) => {
    setEditPlan(prev => ({ ...prev, color: color.hex }));
    // setEditColorPickerOpen(false); // Otomatik kapanma kaldırıldı
  };
  const handleEmojiClick = (emojiData) => {
    setNewPlan(prev => ({ ...prev, emoji: emojiData.emoji }));
    setEmojiPickerOpen(false);
  };
  const handleEditEmojiClick = (emojiData) => {
    setEditPlan(prev => ({ ...prev, emoji: emojiData.emoji }));
    setEditEmojiPickerOpen(false);
  };

  const handleCreatePlan = async () => {
    if (!newPlan.title) return alert('Plan adı zorunlu');
    try {
      const res = await fetch(API_ENDPOINTS.plans.create(), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          ...newPlan,
          price_monthly: newPlan.price_monthly === '' ? null : newPlan.price_monthly,
          price_yearly: newPlan.price_yearly === '' ? null : newPlan.price_yearly
        })
      });
      if (!res.ok) throw new Error('Plan eklenemedi');
      setNewPlan({ title: '', price_monthly: '', price_yearly: '', color: '', emoji: '' });
      setAddModalOpen(false);
      fetchPlans();
    } catch (err) {
      alert('Plan eklenemedi: ' + (err.message));
    }
  };

  const handleEditPlan = (plan) => {
    setEditPlanId(plan.id);
    setEditPlan({
      title: plan.title,
      price_monthly: plan.price_monthly,
      price_yearly: plan.price_yearly,
      color: plan.color,
      emoji: plan.emoji
    });
    setEditModalOpen(true);
  };

  const handleEditPlanChange = (field, value) => {
    setEditPlan(prev => ({ ...prev, [field]: value }));
  };

  const handleUpdatePlan = async () => {
    if (!editPlan.title) return alert('Plan adı zorunlu');
    try {
      const res = await fetch(API_ENDPOINTS.plans.update(editPlanId), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          ...editPlan,
          price_monthly: editPlan.price_monthly === '' ? null : editPlan.price_monthly,
          price_yearly: editPlan.price_yearly === '' ? null : editPlan.price_yearly
        })
      });
      if (!res.ok) throw new Error('Plan güncellenemedi');
      setEditPlanId(null);
      setEditPlan({ title: '', price_monthly: '', price_yearly: '', color: '', emoji: '' });
      setEditModalOpen(false); // Modalı kapat
      fetchPlans();
    } catch (err) {
      alert('Plan güncellenemedi: ' + (err.message));
    }
  };

  const handleCancelEdit = () => {
    setEditPlanId(null);
    setEditPlan({ title: '', price_monthly: '', price_yearly: '', color: '', emoji: '' });
    setEditModalOpen(false);
  };

  const handleOpenFeatureModal = () => {
    setFeatureModalOpen(true);
    setFeatureKey('');
    setFeatureValue('');
    setFeatureSyncAll(true);
  };
  const handleCloseFeatureModal = () => setFeatureModalOpen(false);

  const handleAddFeatureAllPlans = async () => {
    if (!featureKey) return alert('Özellik anahtarı girin');
    try {
      // plans[0] üzerinden bir id al, backend syncAll ile tümüne uygular
      const planId = plans[0]?.id;
      if (!planId) return alert('Hiç plan yok!');
      const res = await fetch(API_ENDPOINTS.plans.addFeature(planId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          feature_key: featureKey,
          feature_value: featureValue,
          syncAll: featureSyncAll
        })
      });
      if (!res.ok) throw new Error('Özellik eklenemedi');
      fetchPlans();
      setFeatureModalOpen(false);
    } catch (err) {
      alert('Özellik eklenemedi: ' + (err.message));
    }
  };

  // Shopier entegrasyonu fonksiyonları
  const handleCreateShopierProduct = async (planId) => {
    setShopierLoading(prev => ({ ...prev, [planId]: true }));
    try {
      const res = await fetch(API_ENDPOINTS.shopier.createPlanAsProduct(planId), {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Shopier ürün oluşturulamadı');
      }
      
      const data = await res.json();
      await fetchPlans(); // Planları yenile
      toast.success('Plan Shopier\'da ürün olarak oluşturuldu');
    } catch (err) {
      toast.error('Shopier ürün oluşturulamadı: ' + err.message);
    } finally {
      setShopierLoading(prev => ({ ...prev, [planId]: false }));
    }
  };

  const handleUpdateShopierProduct = async (planId) => {
    setShopierLoading(prev => ({ ...prev, [planId]: true }));
    try {
      const res = await fetch(API_ENDPOINTS.shopier.updatePlanAsProduct(planId), {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        }
      });
      
      if (!res.ok) {
        const errorData = await res.json();
        throw new Error(errorData.error || 'Shopier ürün güncellenemedi');
      }
      
      toast.success('Plan Shopier\'da güncellendi');
    } catch (err) {
      toast.error('Shopier ürün güncellenemedi: ' + err.message);
    } finally {
      setShopierLoading(prev => ({ ...prev, [planId]: false }));
    }
  };

  const handleFeatureEditInput = (planId, featureKey, value) => {
    setFeatureEditInputs(prev => ({
      ...prev,
      [planId]: {
        ...(prev[planId] || {}),
        [featureKey]: value
      }
    }));
  };

  const handleSaveFeatureEdit = async (planId, featureKey) => {
    // Sadece değer değiştiyse güncelle
    const currentValue = plans.find(p => p.id === planId)?.features.find(f => f.feature_key === featureKey)?.feature_value;
    const newValue = featureEditInputs[planId]?.[featureKey];
    if (newValue === undefined || newValue === currentValue) return; // Değer değişmediyse hiçbir şey yapma
    setFeatureEditLoading(prev => ({
      ...prev,
      [planId]: {
        ...(prev[planId] || {}),
        [featureKey]: true
      }
    }));
    try {
      const res = await fetch(API_ENDPOINTS.plans.addFeature(planId), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('authToken')}`
        },
        body: JSON.stringify({
          feature_key: featureKey,
          feature_value: newValue,
          syncAll: false
        })
      });
      if (!res.ok) throw new Error('Özellik güncellenemedi');
      fetchPlans();
      toast.success('Özellik güncellendi');
    } catch (err) {
      toast.error('Özellik güncellenemedi: ' + (err.message));
    } finally {
      setFeatureEditLoading(prev => ({
        ...prev,
        [planId]: {
          ...(prev[planId] || {}),
          [featureKey]: false
        }
      }));
    }
  };

  if (userEmail !== 'mertdgn35@gmail.com') {
    return <Box p={4}><Typography color="error">Bu sayfaya sadece admin erişebilir.</Typography></Box>;
  }

  return (
    <Box p={4}>
      <Box display="flex" alignItems="center" justifyContent="space-between" mb={3}>
        <Typography variant="h4" fontWeight={700}>Paket Ayarları</Typography>
        <Box display="flex" gap={2}>
          <Button variant="outlined" onClick={handleOpenFeatureModal} sx={{ borderRadius: 3, fontWeight: 600, fontSize: 16, px: 3, py: 1.2 }}>Özellik Ekle</Button>
          <Button variant="contained" onClick={handleOpenAddModal} sx={{ borderRadius: 3, fontWeight: 600, fontSize: 16, px: 3, py: 1.2 }}>Yeni Plan Ekle</Button>
        </Box>
      </Box>
      {/* Özellik ekleme popup */}
      <Dialog open={featureModalOpen} onClose={handleCloseFeatureModal} maxWidth="xs" fullWidth>
        <DialogTitle>Özellik Ekle</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Özellik Anahtarı" value={featureKey} onChange={e => setFeatureKey(e.target.value)} size="small" fullWidth />
            <TextField label="Değer" value={featureValue} onChange={e => setFeatureValue(e.target.value)} size="small" fullWidth />
            <FormControlLabel
              control={<Checkbox checked={featureSyncAll} onChange={e => setFeatureSyncAll(e.target.checked)} />}
              label="Tüm paketlere uygula"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseFeatureModal}>Vazgeç</Button>
          <Button variant="contained" onClick={handleAddFeatureAllPlans}>Ekle</Button>
        </DialogActions>
      </Dialog>
      {/* Plan ekleme popup */}
      <Dialog open={addModalOpen} onClose={handleCloseAddModal} maxWidth="xs" fullWidth>
        <DialogTitle>Yeni Plan Ekle</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Plan Adı" value={newPlan.title} onChange={e => handleNewPlanChange('title', e.target.value)} size="small" fullWidth />
            <TextField label="Aylık Fiyat" value={newPlan.price_monthly} onChange={e => handleNewPlanChange('price_monthly', e.target.value)} size="small" type="number" fullWidth />
            <TextField label="Yıllık Fiyat" value={newPlan.price_yearly} onChange={e => handleNewPlanChange('price_yearly', e.target.value)} size="small" type="number" fullWidth />
            <Box display="flex" alignItems="center" gap={2}>
              <Button variant="outlined" onClick={() => setColorPickerOpen(!colorPickerOpen)} sx={{ minWidth: 40, p: 0, borderRadius: 2, border: '2px solid', borderColor: newPlan.color || '#7b6ada', bgcolor: newPlan.color || '#fff' }}>
                <Box width={28} height={28} borderRadius={2} bgcolor={newPlan.color || '#fff'} border="1px solid #ccc" />
              </Button>
              <span style={{ fontWeight: 500 }}>Renk Seç</span>
              {colorPickerOpen && (
                <ClickAwayListener onClickAway={() => setColorPickerOpen(false)}>
                  <Box position="absolute" zIndex={10} mt={5}>
                    <SketchPicker color={newPlan.color || '#7b6ada'} onChange={handleColorChange} />
                  </Box>
                </ClickAwayListener>
              )}
              <Button variant="outlined" onClick={() => setEmojiPickerOpen(!emojiPickerOpen)} sx={{ minWidth: 40, p: 0, borderRadius: 2, ml: 2 }}>
                <span style={{ fontSize: 24 }}>{newPlan.emoji || '😀'}</span>
              </Button>
              <span style={{ fontWeight: 500 }}>Emoji Seç</span>
              {emojiPickerOpen && (
                <Box position="absolute" zIndex={10} mt={5}>
                  <EmojiPicker onEmojiClick={handleEmojiClick} width={350} height={400} />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAddModal}>Vazgeç</Button>
          <Button variant="contained" onClick={handleCreatePlan}>Ekle</Button>
        </DialogActions>
      </Dialog>
      {/* Plan düzenleme popup */}
      <Dialog open={editModalOpen} onClose={handleCancelEdit} maxWidth="xs" fullWidth>
        <DialogTitle>Paket Düzenle</DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField label="Plan Adı" value={editPlan.title} onChange={e => handleEditPlanChange('title', e.target.value)} size="small" fullWidth />
            <TextField label="Aylık Fiyat" value={editPlan.price_monthly} onChange={e => handleEditPlanChange('price_monthly', e.target.value)} size="small" type="number" fullWidth />
            <TextField label="Yıllık Fiyat" value={editPlan.price_yearly} onChange={e => handleEditPlanChange('price_yearly', e.target.value)} size="small" type="number" fullWidth />
            <Box display="flex" alignItems="center" gap={2}>
              <Button variant="outlined" onClick={() => setEditColorPickerOpen(!editColorPickerOpen)} sx={{ minWidth: 40, p: 0, borderRadius: 2, border: '2px solid', borderColor: editPlan.color || '#7b6ada', bgcolor: editPlan.color || '#fff' }}>
                <Box width={28} height={28} borderRadius={2} bgcolor={editPlan.color || '#fff'} border="1px solid #ccc" />
              </Button>
              <span style={{ fontWeight: 500 }}>Renk Seç</span>
              {editColorPickerOpen && (
                <ClickAwayListener onClickAway={() => setEditColorPickerOpen(false)}>
                  <Box position="absolute" zIndex={10} mt={5}>
                    <SketchPicker color={editPlan.color || '#7b6ada'} onChange={handleEditColorChange} />
                  </Box>
                </ClickAwayListener>
              )}
              <Button variant="outlined" onClick={() => setEditEmojiPickerOpen(!editEmojiPickerOpen)} sx={{ minWidth: 40, p: 0, borderRadius: 2, ml: 2 }}>
                <span style={{ fontSize: 24 }}>{editPlan.emoji || '😀'}</span>
              </Button>
              <span style={{ fontWeight: 500 }}>Emoji Seç</span>
              {editEmojiPickerOpen && (
                <Box position="absolute" zIndex={10} mt={5}>
                  <EmojiPicker onEmojiClick={handleEditEmojiClick} width={350} height={400} />
                </Box>
              )}
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCancelEdit}>Vazgeç</Button>
          <Button variant="contained" onClick={handleUpdatePlan}>Kaydet</Button>
        </DialogActions>
      </Dialog>
      {loading ? <Typography>Yükleniyor...</Typography> : (
        plans.map(plan => (
          <Paper key={plan.id} sx={{ mb: 4, p: 2 }}>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{plan.title} {plan.emoji} <span style={{ color: plan.color }}>{plan.color}</span></Typography>
              <Box display="flex" gap={1}>
                {/* Shopier entegrasyonu butonları */}
                {!plan.shopier_product_id ? (
                  <IconButton 
                    color="success" 
                    onClick={() => handleCreateShopierProduct(plan.id)}
                    disabled={shopierLoading[plan.id]}
                    title="Shopier'da ürün oluştur"
                  >
                    {shopierLoading[plan.id] ? <SyncIcon /> : <ShopIcon />}
                  </IconButton>
                ) : (
                  <IconButton 
                    color="info" 
                    onClick={() => handleUpdateShopierProduct(plan.id)}
                    disabled={shopierLoading[plan.id]}
                    title="Shopier'da güncelle"
                  >
                    {shopierLoading[plan.id] ? <SyncIcon /> : <SyncIcon />}
                  </IconButton>
                )}
                <IconButton color="primary" onClick={() => handleEditPlan(plan)}><EditIcon /></IconButton>
                <IconButton color="error" onClick={() => handleDeletePlan(plan.id)}><DeleteIcon /></IconButton>
              </Box>
            </Box>
            <Typography>Fiyat: {plan.price_monthly}₺ / ay, {plan.price_yearly}₺ / yıl</Typography>
            {/* Plan düzenleme formu */}
            {editPlanId === plan.id && (
              <Box display="flex" gap={2} flexWrap="wrap" alignItems="center" mt={2}>
                <TextField label="Plan Adı" value={editPlan.title} onChange={e => handleEditPlanChange('title', e.target.value)} size="small" />
                <TextField label="Aylık Fiyat" value={editPlan.price_monthly} onChange={e => handleEditPlanChange('price_monthly', e.target.value)} size="small" type="number" />
                <TextField label="Yıllık Fiyat" value={editPlan.price_yearly} onChange={e => handleEditPlanChange('price_yearly', e.target.value)} size="small" type="number" />
                <TextField label="Renk" value={editPlan.color} onChange={e => handleEditPlanChange('color', e.target.value)} size="small" placeholder="#7b6ada" />
                <TextField label="Emoji" value={editPlan.emoji} onChange={e => handleEditPlanChange('emoji', e.target.value)} size="small" placeholder="🎉" />
                <Button variant="contained" onClick={handleUpdatePlan}>Kaydet</Button>
                <Button variant="outlined" onClick={handleCancelEdit}>Vazgeç</Button>
              </Box>
            )}
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Özellik Anahtarı</TableCell>
                    <TableCell>Değer</TableCell>
                    <TableCell>İşlem</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {plan.features.map(f => (
                    <TableRow key={f.id}>
                      <TableCell>{f.feature_key}</TableCell>
                      <TableCell>
                        <TextField
                          size="small"
                          value={featureEditInputs[plan.id]?.[f.feature_key] ?? f.feature_value}
                          onChange={e => handleFeatureEditInput(plan.id, f.feature_key, e.target.value)}
                          sx={{ minWidth: 120 }}
                        />
                      </TableCell>
                      <TableCell>
                        <IconButton
                          color="primary"
                          onClick={() => {
                            const currentValue = plans.find(p => p.id === plan.id)?.features.find(f2 => f2.feature_key === f.feature_key)?.feature_value;
                            const newValue = featureEditInputs[plan.id]?.[f.feature_key];
                            if (newValue !== undefined && newValue !== currentValue) {
                              handleSaveFeatureEdit(plan.id, f.feature_key);
                            }
                          }}
                          disabled={featureEditLoading[plan.id]?.[f.feature_key] || featureEditInputs[plan.id]?.[f.feature_key] === undefined || featureEditInputs[plan.id]?.[f.feature_key] === f.feature_value}
                        >
                          {featureEditInputs[plan.id]?.[f.feature_key] !== undefined && featureEditInputs[plan.id]?.[f.feature_key] !== f.feature_value ? (
                            <CheckIcon />
                          ) : (
                            <AddIcon />
                          )}
                        </IconButton>
                        <IconButton color="error" onClick={() => handleDeleteFeature(plan.id, f.feature_key)}><DeleteIcon /></IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                  {/* Özellik ekleme satırı kaldırıldı */}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))
      )}
      <Dialog open={deleteFeatureDialog.open} onClose={() => setDeleteFeatureDialog({ open: false, planId: null, featureKey: null })}>
        <DialogTitle>Özelliği Sil</DialogTitle>
        <DialogContent>
          <Typography>Bu özelliği silmek istediğinizden emin misiniz? Bu işlem geri alınabilir.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteFeatureDialog({ open: false, planId: null, featureKey: null })}>İptal</Button>
          <Button onClick={confirmDeleteFeature} color="error" variant="contained">Sil</Button>
        </DialogActions>
      </Dialog>
      <Dialog open={deletePlanDialog.open} onClose={() => setDeletePlanDialog({ open: false, planId: null })}>
        <DialogTitle>Planı Sil</DialogTitle>
        <DialogContent>
          <Typography>Bu planı silmek veya pasif yapmak istediğinizden emin misiniz? Bu işlem geri alınabilir.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeletePlanDialog({ open: false, planId: null })}>İptal</Button>
          <Button onClick={confirmDeletePlan} color="error" variant="contained">Sil</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PlanSettings; 